/** Preview — the wizard run live against the project's preview database, by
    the same runner and platform host the Run view mounts. It runs the SAVED
    model (the runner reads it through the render read, as every runtime does),
    so unsaved changes are saved first, like the other designers' previews.

    `WizardLivePreviewPane` is the same surface for the workbench's Live
    Preview tab, with no designer around it.

    The INSPECTOR (collapsible, beside the run) shows what the runner holds as
    it goes — the record's header, the current line and the lines per form,
    the variables and inputs, the cursor and call stack, the last issues —
    read from the runner's `onState`, never written back. */

import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { ChevronDown, ChevronRight, Loader2, PanelRightClose, PanelRightOpen, Play, RotateCcw, Save } from 'lucide-react'
import { parseApiError } from '@twasta/page-runtime/txn'
import type { WizardRunnerState } from '@twasta/page-runtime/wizard/contract'
import { loadWizardObject } from '@/api/wizards'
import { livePreviewDeploy } from '@/livepreview/api'
import { PlatformWizard, WizardNote } from '@/livepreview/runtime/wizardHost'
import type { WizardInput } from './types'
import { Field, TextInput, hintStyle, smallBtn } from './ui'

/** Make sure the base transaction's tables exist in the preview database —
 *  what the transaction's own Live Preview does on open. */
function useDeployedBase(project: string, baseObject: string | undefined) {
  const [state, setState] = useState<'idle' | 'deploying' | 'ready' | 'failed'>('idle')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!project || !baseObject) { setState('ready'); return undefined }
    let cancelled = false
    setState('deploying')
    livePreviewDeploy(project, baseObject)
      .then(() => { if (!cancelled) setState('ready') })
      .catch((err) => {
        if (cancelled) return
        setError(parseApiError(err).message || 'The base transaction could not be deployed')
        setState('failed')
      })
    return () => { cancelled = true }
  }, [project, baseObject])
  return { state, error }
}

function LaunchBar({ inputs, values, onValues, onStart, onRestart, inspector }: {
  inputs: WizardInput[]
  values: Record<string, string>
  onValues: (v: Record<string, string>) => void
  onStart: () => void
  onRestart: () => void
  inspector?: { open: boolean; toggle: () => void }
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap', padding: '8px 10px',
      borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
    }}>
      {inputs.map((inp) => (
        <div key={inp.name} style={{ width: 170 }}>
          <Field label={`${inp.label || inp.name}${inp.required ? ' *' : ''}`}>
            <TextInput value={values[inp.name] ?? ''} placeholder={inp.default !== undefined ? String(inp.default) : ''}
              onChange={(v) => onValues({ ...values, [inp.name]: v })} />
          </Field>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button type="button" style={smallBtn} onClick={onStart} title="Launch with these inputs, as a URL / action button would">
          <Play size={12} /> {inputs.length ? 'Launch with these inputs' : 'Launch'}
        </button>
        <button type="button" style={smallBtn} onClick={onRestart}><RotateCcw size={12} /> Start over</button>
        {inspector && (
          <button type="button" style={smallBtn} onClick={inspector.toggle}
            title="What the run holds: record, current line, variables, cursor, issues">
            {inspector.open ? <PanelRightClose size={12} /> : <PanelRightOpen size={12} />}
            {inspector.open ? 'Hide inspector' : 'Inspector'}
          </button>
        )}
      </div>
    </div>
  )
}

/** Short, readable form of one value for the inspector. */
function show(v: unknown): string {
  if (v === undefined) return '—'
  if (v === null) return 'null'
  if (typeof v === 'string') return v === '' ? '""' : v
  try { return JSON.stringify(v) } catch { return String(v) }
}

/** Visible columns of a row (the runner's own control keys left out). */
function plainEntries(row: Record<string, unknown> | null | undefined): Array<[string, unknown]> {
  return Object.entries(row || {}).filter(([k]) => !k.startsWith('_') && k !== '__children')
}

function Section({ title, count, children, open: initial = true }: {
  title: string; count?: number | string; children: ReactNode; open?: boolean
}) {
  const [open, setOpen] = useState(initial)
  return (
    <div style={{ borderBottom: '1px solid var(--color-border)' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 4, width: '100%', padding: '6px 8px', border: 'none',
        background: 'transparent', color: 'var(--color-text)', cursor: 'pointer', fontSize: 13,
        fontWeight: 600, textAlign: 'left',
      }}>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span style={{ flex: 1 }}>{title}</span>
        {count !== undefined && <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>{count}</span>}
      </button>
      {open && <div style={{ padding: '0 8px 8px 22px' }}>{children}</div>}
    </div>
  )
}

function KV({ entries }: { entries: Array<[string, unknown]> }) {
  if (!entries.length) return <div style={hintStyle}>None.</div>
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(70px, max-content) 1fr', gap: '2px 8px', fontSize: 13 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <div style={{ color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>{k}</div>
          <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{show(v)}</div>
        </div>
      ))}
    </div>
  )
}

/** What the runner holds right now (read-only). */
function FrameInspector({ state }: { state: WizardRunnerState | null }) {
  if (!state || !state.frame) {
    return <div style={{ ...hintStyle, padding: 10 }}>Start the wizard to see its record and variables here.</div>
  }
  const { run, frame, issues } = state
  const details = frame.record?.details || {}
  const lineForm = frame.line_form || ''
  const lineRows = (details[lineForm] || [])
  const current = frame.line_index != null ? lineRows[frame.line_index] : null
  const stack = run?.stack || []
  return (
    <div style={{ fontSize: 13 }}>
      <Section title="Cursor" count={state.phase}>
        <KV entries={[
          ['step', frame.cursor],
          ['mode', frame.mode],
          ['txn_id', frame.txn_id ?? null],
          ['saved', frame.save_status || 'none'],
          ['changed', !!frame.dirty],
          ['run', run?.run_id],
          ...(run?.waiting_on ? [['waiting on', run.waiting_on] as [string, unknown]] : []),
        ]} />
        {stack.length > 1 && (
          <div style={{ marginTop: 6 }}>
            <div style={{ ...hintStyle, marginBottom: 2 }}>Call stack (outermost first)</div>
            {stack.map((f, i) => (
              <div key={i} style={{ fontFamily: 'monospace', fontSize: 13 }}>
                {i + 1}. {f.wizard} @ {f.cursor}{f.pending_call ? ` — waiting on ${f.pending_call.step_id}` : ''}
              </div>
            ))}
          </div>
        )}
        {(frame.history || []).length > 0 && (
          <div style={{ ...hintStyle, marginTop: 6, wordBreak: 'break-word' }}>
            Path: {frame.history.slice(-12).join(' → ')}
          </div>
        )}
      </Section>
      <Section title="Header" count={plainEntries(frame.record?.header).length}>
        <KV entries={plainEntries(frame.record?.header)} />
      </Section>
      <Section title={current ? `Current line (form ${lineForm}, #${(frame.line_index ?? 0) + 1})` : 'Current line'}>
        {current ? <KV entries={plainEntries(current)} /> : <div style={hintStyle}>No line is current.</div>}
      </Section>
      <Section title="Lines" count={Object.values(details).reduce((n, rows) => n + (rows || []).filter((r) => !r._deleted).length, 0)}>
        {Object.keys(details).length === 0 ? <div style={hintStyle}>None.</div> : (
          <KV entries={Object.entries(details).map(([form, rows]) => {
            const live = (rows || []).filter((r) => !r._deleted)
            const kids = live.reduce((n, r) => n + Object.values((r.__children as Record<string, unknown[]> | undefined) || {})
              .reduce((m, x) => m + (x || []).length, 0), 0)
            const removed = (rows || []).length - live.length
            return [`form ${form}`, `${live.length} line${live.length === 1 ? '' : 's'}`
              + (removed ? `, ${removed} removed` : '') + (kids ? `, ${kids} child row${kids === 1 ? '' : 's'}` : '')]
          })} />
        )}
      </Section>
      <Section title="Variables" count={Object.keys(frame.vars || {}).length}>
        <KV entries={Object.entries(frame.vars || {})} />
      </Section>
      <Section title="Inputs" count={Object.keys(frame.input || {}).length} open={false}>
        <KV entries={Object.entries(frame.input || {})} />
      </Section>
      {Object.keys(frame.steps || {}).length > 0 && (
        <Section title="Step values" count={Object.keys(frame.steps || {}).length} open={false}>
          <KV entries={Object.entries(frame.steps || {})} />
        </Section>
      )}
      <Section title="Last issues" count={issues.length}>
        {issues.length === 0 ? <div style={hintStyle}>None.</div> : issues.map((i, k) => (
          <div key={k} style={{
            fontSize: 13, marginBottom: 3,
            color: i.type === 'E' ? 'var(--color-error, #d33)' : 'var(--color-warning, #b45309)',
          }}>
            {i.code ? <code>{i.code}</code> : null}{i.code ? ' ' : ''}{i.message}
            {i.field ? <span style={{ color: 'var(--color-text-secondary)' }}> · {i.form_no ? `form ${i.form_no} ` : ''}{i.row != null ? `line ${i.row + 1} ` : ''}{i.field}</span> : null}
          </div>
        ))}
      </Section>
      {(frame.trail || []).length > 0 && (
        <Section title="Trail" count={(frame.trail || []).length} open={false}>
          {(frame.trail || []).slice(-15).reverse().map((t, k) => (
            <div key={k} style={{ fontFamily: 'monospace', fontSize: 13 }}>
              {t.kind}{t.step ? ` ${t.step}` : ''}{t.detail ? ` — ${t.detail}` : ''}
            </div>
          ))}
        </Section>
      )}
    </div>
  )
}

function PreviewRunner({ project, objName, inputs, baseObject, inspector = false }: {
  project: string; objName: string; inputs: WizardInput[]; baseObject?: string
  /** Show the frame inspector beside the run (the designer's Preview tab). */
  inspector?: boolean
}) {
  const deployed = useDeployedBase(project, baseObject)
  const [state, setState] = useState<WizardRunnerState | null>(null)
  const [showInspector, setShowInspector] = useState(true)
  const onState = useCallback((s: WizardRunnerState) => setState(s), [])
  const [values, setValues] = useState<Record<string, string>>({})
  const [launched, setLaunched] = useState<{ inputs: Record<string, unknown>; key: number }>({ inputs: {}, key: 0 })
  const launch = () => {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(values)) if (v !== '') out[k] = v
    setLaunched((l) => ({ inputs: out, key: l.key + 1 }))
  }
  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <LaunchBar inputs={inputs} values={values} onValues={setValues} onStart={launch}
        onRestart={() => setLaunched((l) => ({ inputs: l.inputs, key: l.key + 1 }))}
        inspector={inspector ? { open: showInspector, toggle: () => setShowInspector((o) => !o) } : undefined} />
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
      <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        {deployed.state === 'deploying' || deployed.state === 'idle' ? (
          <WizardNote>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} />
            Preparing the preview database…
          </WizardNote>
        ) : deployed.state === 'failed' ? (
          <WizardNote>{deployed.error}</WizardNote>
        ) : (
          <PlatformWizard key={launched.key} project={project} wizard={objName} inputs={launched.inputs}
            onState={inspector ? onState : undefined} />
        )}
      </div>
      {inspector && showInspector && (
        <div data-tw-wizard-inspector="1" style={{
          width: 300, flexShrink: 0, minHeight: 0, overflowY: 'auto',
          borderLeft: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
        }}>
          <div style={{ padding: '6px 8px', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-secondary)' }}>
            Inspector
          </div>
          <FrameInspector state={state} />
        </div>
      )}
      </div>
    </div>
  )
}

export default function PreviewTab({ project, objName, inputs, baseObject, dirty, saving, onSave, canEdit }: {
  project: string
  objName: string
  inputs: WizardInput[]
  baseObject?: string
  dirty: boolean
  saving: boolean
  onSave: () => Promise<boolean>
  canEdit: boolean
}) {
  // What the runner runs is the saved model; a preview that silently ran an
  // older one would answer the wrong question.
  if (dirty) {
    return (
      <WizardNote>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div>The preview runs the saved wizard. Save your changes to preview them.</div>
          {canEdit && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => { void onSave() }} disabled={saving}>
              {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              Save and preview
            </button>
          )}
        </div>
      </WizardNote>
    )
  }
  if (!baseObject) {
    return <WizardNote>Pick the base transaction on Base &amp; Mode first.</WizardNote>
  }
  return (
    <div style={{
      height: '100%', minHeight: 520, display: 'flex', flexDirection: 'column',
      border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden',
    }}>
      <PreviewRunner project={project} objName={objName} inputs={inputs} baseObject={baseObject} inspector />
      <div style={{ ...hintStyle, padding: '4px 10px' }}>
        Runs against the preview database with your own rights checked against this wizard’s grant, as a user would.
      </div>
    </div>
  )
}

/** The workbench Live Preview tab for a wizard node. */
export function WizardLivePreviewPane({ project, objName }: { project: string; objName: string }) {
  const [info, setInfo] = useState<{ inputs: WizardInput[]; base?: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    setInfo(null)
    setError(null)
    loadWizardObject(project, objName)
      .then(({ wizard }) => { if (!cancelled) setInfo({ inputs: wizard.inputs || [], base: wizard.base_object }) })
      .catch((err) => { if (!cancelled) setError(parseApiError(err).message || 'The wizard could not be loaded') })
    return () => { cancelled = true }
  }, [project, objName])
  if (error) return <WizardNote>{error}</WizardNote>
  if (!info) {
    return <WizardNote><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /></WizardNote>
  }
  return <PreviewRunner project={project} objName={objName} inputs={info.inputs} baseObject={info.base} />
}
